import { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { HOSTNAME, isExternalUrl } from "@src/utils";
import useUrlQueryString from "@src/utils/hooks/useUrlQueryString";

import { getWeddingHallInfo } from "@src/api/weddingHall/weddingHall";
import { getVenueInfo } from "@src/api/realtimeBooking/realtimeBooking";
import PartnerReviewList from "@src/pages/partner/detail/_component/review/PartnerReviewList";
import { resetInterWindow } from "@src/@slice/interWindowSlice";
import { setVenueInfo, setInitData, setHallName } from "@src/@slice/realtimeBookingSlice";
import { PartnerIdContext } from "@src/routes/WeddinghallDetail";
import { PartnerType } from "@src/define/partner";

import PartnerTabs from "@src/pages/partner/detail/_component/PartnerTabs";
import InfoTab from "@src/components/weddingHall/infomation/InfoTab";
import PartnerDetailHeader from "@src/pages/partner/detail/_component/PartnerDetailHeader";
import WeddingHallPhotoList from "@src/components/weddingHall/photo/WeddingHallPhotoTab";
import NoticeToast from "@src/components/weddingHall/NoticeToast";
import BottomFloatingButton from "@src/components/weddingHall/BottomFloatingButton";
import ProductTab from "@src/components/weddingHall/product/ProductTab";
import NoData from "@src/components/common/noData/NoData";
import AppHeader from "@src/components/common/appHeader/AppHeader";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
import { IHallDetail } from "@src/types/weddingHall";
import moment from "moment";
import useDuplicateBooking from "@src/utils/hooks/useBookingState";
import { UserContext } from "@src/App";
import { UMOMENT_HALL } from "@src/types/realtimeBooking";
import alarmIcon from "@assets/icons/weddinghall/weekly_alram.png";

const HallInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const query = useUrlQueryString();

  const { partnerId, setPartnerId } = useContext(PartnerIdContext);
  const { isDuplicateBooking, setIsDuplicateBooking, getDuplicateReservation } =
    useDuplicateBooking();

  const refPartnerTabComponentWrap = useRef<HTMLDivElement>(null);
  const refPartnerTabComponentTop = useRef<number>(0);

  const user = useContext(UserContext);
  const partnerUuid = useParams().uuid;
  const tabId = Number(query.get("tabId")) || 1; // 1:정보 , 2:잔여타임, 3:사진, 4:리뷰

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IHallDetail | null>(null); // 전체홀 데이터
  const [useRealtimeBooking, setUseRealtimeBooking] = useState<boolean | null>(null); // 실시간 예약 사용 여부
  const [useAvailableContract, setUseAvailableContract] = useState<boolean | null>(null); // 실시간 계약 사용 여부
  // const [useAutoApproval, setUseAutoApproval] = useState<boolean | null>(null); // 자동 승인 웨딩홀 여부
  const [activeTabId, setActiveTabId] = useState<number>(tabId);
  const [buttonName, setButtonName] = useState(""); //업체 상세페이지 오른쪽 버튼 이름
  // const [isOpenReservationModal, setIsOpenReservationModal] = useState<boolean>(false); // 예약 선택 모달
  const [isOpenWriteReviewModal, setIsOpenWriteReviewModal] = useState<boolean>(false); // 리뷰쓰기 모달

  // 리뷰.포토 등 새창으로 열리는 데이터 초기화
  useEffect(() => {
    localStorage.removeItem("likeChangedPartnerUuid");
    dispatch(resetInterWindow());
  }, [partnerUuid, navigate]);

  useEffect(() => {
    // 실시간 예약 데이터 초기화
    dispatch(setInitData());
    sessionStorage.removeItem("reservationData");
    sessionStorage.removeItem("realtimeInfo");

    if (data === null) {
      //전체 홀 정보 조회
      getWeddingHallInfo(partnerUuid)
        .then((res) => {
          setData(res.item);
          setPartnerId(res.item.partnerId);
          setUseAvailableContract(!!res.item.contractUsed); //잔여타임 선점 사용여부
          setLoading(false);
          return res.item;
        })
        .then((res) => {
          // 실시간예약 사용 + 상담 슬롯 공개인 경우에 실시간예약 정보 조회
          if (res.consultingUsed && res.consultingSlotsDisplayed) {
            getVenueInfo(res.partnerId) // 파트너 아이디로 조회하고 있음...
              .then((res) => {
                if (res.data.data === null) {
                  setUseRealtimeBooking(false); //데이터가 null로 나오면 안됨(서버이슈) -> 혹시 그럴 경우 방문예약으로 연결
                } else {
                  dispatch(
                    setVenueInfo({
                      data: {
                        ...res.data.data,
                        useAutoApproval: res.data.data?.consultingUseAutoApproval || false,
                      },
                    }),
                  );
                }
              });
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
    }
  }, [data]);

  // URL 파라미터가 바뀔 때 activeTabId 업데이트
  useEffect(() => {
    const currentTabId = Number(query.get("tabId")) || 1;
    setActiveTabId(currentTabId);
  }, [query]);

  useEffect(() => {
    //잔여타임 탭 상단 이동
    if (tabId === 2) {
      window.scrollTo({
        top: 895,
        left: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // 버튼 이름 설정
  useEffect(() => {
    if (loading === false) {
      switch (activeTabId) {
        case 1:
        case 4:
          data.bookingState ? setButtonName("방문 예약 신청") : setButtonName("전화걸기"); // bookingState : 방문예약 가능여부
          break;
        case 2:
        case 3:
          setButtonName("");
          break;
      }
    }
  }, [loading, activeTabId]);

  // '방문 예약하기' 버튼 클릭 시 이벤트 설정
  const handleRightBtnClick = async () => {
    //유모멘트 웨딩홀의 경우 무조건 즉시예약만 가능
    if (partnerUuid && UMOMENT_HALL.has(partnerUuid)) {
      dispatch(setHallName({ name: data.profile.name }));
      window.location.href = isExternalUrl(
        `${HOSTNAME}/booking/hall?partnerId=${partnerId}&partnerUuids=${partnerUuid}&hallName=${data.profile.name}&umoment=true&hidetop=true`,
      );
    } else if (buttonName === "전화걸기") {
      window.location.href = `tel:${data.profile.tel}`;
    } else {
      await getDuplicateReservation(partnerUuid, user.me?.uuid, partnerId, true, data.profile.name);
    }
  };

  const handleClickTabButton = (tabId: number) => {
    setActiveTabId(tabId);
    navigate(`${location.pathname}?tabId=${tabId}`, { replace: true });
    const curScrollY = window.scrollY || window.document.body.scrollTop || window.pageYOffset;
    if (curScrollY > refPartnerTabComponentTop.current) {
      window.scrollTo(0, refPartnerTabComponentTop.current + 55);
    }
  };

  return (
    <div className="weddinghall-info-page">
      {!loading ? (
        <>
          <PartnerDetailHeader
            partnerName={data.profile?.name || data.bizName}
            partnerUuid={partnerUuid}
            isLike={data.like}
            shareLink={data.shareLink}
            partnerType={PartnerType.WeddingHall}
          />
          <div
            // ref={refTabs}
            className={`partner-tabs-container `}
            style={{ position: "fixed", top: "55px", zIndex: 10, width: "100%" }}
          >
            {useAvailableContract ? (
              <PartnerTabs
                className="weddinghall-tabs"
                tabItems={[
                  { tabId: 1, text: "정보" },
                  { tabId: 2, text: "즉시계약" },
                  { tabId: 3, text: "사진" },
                  { tabId: 4, text: "리뷰" },
                ]}
                activeTabId={activeTabId}
                onClickTabItem={(tabId) => handleClickTabButton(tabId)}
              />
            ) : (
              <PartnerTabs
                tabItems={[
                  { tabId: 1, text: "정보" },
                  { tabId: 3, text: "사진" },
                  { tabId: 4, text: "리뷰" },
                ]}
                activeTabId={activeTabId}
                onClickTabItem={(tabId) => handleClickTabButton(tabId)}
              />
            )}
          </div>

          <div className="weddinghall-tab-content-wrap" ref={refPartnerTabComponentWrap}>
            <Tap tabId={activeTabId} setActiveTabId={setActiveTabId} info={data} />
          </div>

          {data.profile?.notice &&
            activeTabId === 1 &&
            moment().isBefore(data.expirationDateOfNotice) && (
              <NoticeToast
                hasMark={true}
                notice={data.profile.notice}
                btnName="확인"
                bottom={100}
              />
            )}
          <BottomFloatingButton
            hallPayload={data}
            partnerId={partnerId}
            buttonName={buttonName}
            isOpenWriteReviewModal={isOpenWriteReviewModal}
            onClose={() => setIsOpenWriteReviewModal(false)} //리뷰 모달 닫기
            handleRightBtnClick={handleRightBtnClick}
            isDuplicateBooking={isDuplicateBooking}
            setIsDuplicateBooking={(bool) => setIsDuplicateBooking(bool)}
          >
            {activeTabId === 1 && data.isWeeklyReservationActive === true && (
              <button
                className="btn btn-giant btn-gray50 btn-tiny week-alarm-btn"
                onClick={() => {
                  window.location.href = isExternalUrl(
                    `${HOSTNAME}/weekly-alarm/apply?partnerId=${partnerId}&partnerUuids=${partnerUuid}&partnerName=${data.profile.name}`,
                  );
                }}
              >
                <img src={alarmIcon} alt="알림신청" width={24} height={24} />
                알림신청
              </button>
            )}
            {activeTabId === 4 && (
              <button
                className="btn btn-giant btn-red0"
                onClick={() => {
                  window.location.href = isExternalUrl(
                    `${HOSTNAME}/review/write?partnerUuid=${partnerUuid}`,
                  );
                }}
              >
                리뷰쓰기
              </button>
            )}
          </BottomFloatingButton>
        </>
      ) : (
        <>
          {data === null ? (
            <LoadingSpinner backgroundColor="transparent" position="absolute" />
          ) : (
            <>
              <AppHeader type="stack" />
              <NoData title={"정보가 없습니다"} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default HallInfo;

const Tap = ({ tabId, setActiveTabId, info }) => {
  switch (tabId) {
    case 1:
      return (
        <InfoTab
          type="default"
          data={info}
          comparisonData={null}
          onClickTab={(idx: number) => setActiveTabId(idx)}
        />
      );
    case 2:
      return <ProductTab />;
    case 3:
      return <WeddingHallPhotoList tabId={0} partner={info} />;
    case 4:
      return <PartnerReviewList partnerUuid={info.uuid} partnerType={info.partnerType} />; //앱웹컴포넌트 재사용
    default:
      return (
        <InfoTab
          type="default"
          data={info}
          comparisonData={null}
          onClickTab={(idx: number) => setActiveTabId(idx)}
        />
      );
  }
};
